import React, { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, TextField, MenuItem, Alert,
} from '@mui/material';
import { ClinicDialog } from '../dialogs/ClinicDialog';
import { CLINIC_DIALOG_SX } from '../../constants/popupStyles';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { wellnessItemService, WellnessItem } from '../../services/wellnessItemService';
import { getApiError } from '../../utils/apiError';
import { resetColorHandler, colorTooltipSx, applyColorTooltips } from '../editor/quillColorReset';

const QUILL_MODULES = {
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      ['link'],
      [{ color: [] }, { background: [] }],
      ['clean'],
      ['resetColor'],
    ],
    handlers: {
      color(this: any, value: string) {
        const range = this.quill.getSelection();
        const colorValue = value || 'rgb(0, 0, 0)';
        if (range && range.length > 0) {
          this.quill.formatText(range.index, range.length, 'color', colorValue, 'user');
        } else {
          this.quill.format('color', colorValue, 'user');
        }
      },
      resetColor: resetColorHandler,
    },
  },
};
const QUILL_FORMATS = ['header', 'bold', 'italic', 'underline', 'strike', 'list', 'bullet', 'link', 'color', 'background'];
const isQuillEmpty = (val: string) => !val || val === '<p><br></p>';

const ITEM_TYPES = [
  { value: 'item', label: 'Item' },
  { value: 'category', label: 'Category' },
];

const MEDIA_TYPES = [
  { value: '', label: 'None' },
  { value: 'picture', label: 'Picture' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'content', label: 'Content' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  item: WellnessItem | null;
  onSaved: (updated: WellnessItem) => void;
}

const WellnessItemNestedEditDialog: React.FC<Props> = ({ open, onClose, item, onSaved }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState('item');
  const [mediaType, setMediaType] = useState('picture');
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [duration, setDuration] = useState('');
  const [text, setText] = useState('');
  const [pictureFileId, setPictureFileId] = useState<number | null>(null);
  const [picturePreviewUrl, setPicturePreviewUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [nameError, setNameError] = useState('');

  const isPicture = mediaType === 'picture';
  const hasDuration = mediaType === 'video' || mediaType === 'audio';
  const hasText = mediaType !== 'content' && mediaType !== '';
  const hasUrl = mediaType !== 'picture' && mediaType !== '';

  // Label the colour toolbar controls (incl. the custom "Clear color" button)
  // once Quill has rendered the text editor.
  const textEditorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open || !hasText) return;
    const id = requestAnimationFrame(() => applyColorTooltips(textEditorRef.current));
    return () => cancelAnimationFrame(id);
  }, [open, hasText, mediaType]);

  useEffect(() => {
    if (!open || !item) return;
    setName(item.name || '');
    setType(item.type || 'item');
    setMediaType(item.data?.media_type || '');
    setUrl(item.data?.url || '');
    setTitle(item.data?.title || '');
    setSubtitle(item.data?.subtitle || '');
    setDuration(item.data?.duration || '');
    setText(item.data?.text || '');
    const fid = item.data?.picture_file_id ?? null;
    setPictureFileId(fid);
    setPicturePreviewUrl(null);
    setNameError('');
    setSaveError('');
    if (fid) {
      wellnessItemService.getPictureBlobUrl(fid).then(setPicturePreviewUrl).catch(() => {});
    }
  }, [open, item]);

  const handlePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { file_id } = await wellnessItemService.uploadPicture(file);
      setPictureFileId(file_id);
      setPicturePreviewUrl(URL.createObjectURL(file));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setNameError('Name is required'); return; }
    if (!item) return;
    setSaving(true);
    setSaveError('');
    try {
      const mt = mediaType;
      const _isPicture = mt === 'picture';
      const _hasDuration = mt === 'video' || mt === 'audio';
      const _hasText = mt !== 'content' && mt !== '';
      const _hasUrl = mt !== 'picture' && mt !== '';
      const data: any = {
        media_type: mt || null,
        title: title.trim() || null,
        subtitle: subtitle.trim() || null,
        picture_file_id: _isPicture ? pictureFileId : null,
        url: _hasUrl ? (url.trim() || null) : null,
        text: _hasText ? (isQuillEmpty(text) ? null : text) : null,
        duration: _hasDuration ? (duration.trim() || null) : null,
        ...(item.data?.main_item !== undefined ? { main_item: item.data.main_item } : {}),
        ...(item.data?.items !== undefined ? { items: item.data.items } : {}),
      };
      const updated = await wellnessItemService.update(item.id, { name: name.trim(), type: type as any, data });
      onSaved(updated);
    } catch (err: any) {
      const msg = getApiError(err, 'An error occurred') || err?.message || 'Failed to save. Please try again.';
      setSaveError(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ClinicDialog
      open={open}
      onClose={onClose}
      title="Edit Wellness Item"
      closeButtonSide="left"
      onSubmit={handleSubmit}
      actions={
        <>
          <Button type="submit" variant="contained" sx={CLINIC_DIALOG_SX.primaryButton} disabled={saving}>
            {saving ? 'Saving...' : 'Update'}
          </Button>
          <Button onClick={onClose} sx={CLINIC_DIALOG_SX.cancelButton}>Cancel</Button>
        </>
      }
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 420 }}>
        {saveError && <Alert severity="error" sx={{ mb: 1 }}>{saveError}</Alert>}
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Name</Typography>
          <TextField
            value={name} onChange={(e) => { setName(e.target.value); if (e.target.value.trim()) setNameError(''); }}
            fullWidth size="small" error={!!nameError} helperText={nameError}
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Type</Typography>
          <TextField select value={type} onChange={(e) => setType(e.target.value)} fullWidth size="small"
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
            >
            {ITEM_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Media Type</Typography>
          <TextField select value={mediaType} onChange={(e) => setMediaType(e.target.value)} fullWidth size="small"
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }}
            >
            {MEDIA_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
          </TextField>
        </Box>
        {/* Picture */}
        <Box sx={{ display: isPicture ? undefined : 'none' }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Picture</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1 }}>
              {picturePreviewUrl && (
                <Box component="img" src={picturePreviewUrl} alt="preview"
                  sx={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
              )}
              <Button component="label" variant="outlined" size="small" disabled={uploading}>
                {uploading ? 'Uploading...' : picturePreviewUrl ? 'Change Picture' : 'Upload Picture'}
                <input type="file" hidden accept="image/*" onChange={handlePictureChange} />
              </Button>
            </Box>
          </Box>
        </Box>
        {/* URL */}
        <Box sx={{ display: hasUrl ? undefined : 'none' }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>URL</Typography>
            <TextField value={url} onChange={(e) => setUrl(e.target.value)} fullWidth size="small"
              placeholder="https://..."
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
          </Box>
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Title</Typography>
          <TextField value={title} onChange={(e) => setTitle(e.target.value)} fullWidth size="small"
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
        </Box>
        <Box sx={CLINIC_DIALOG_SX.fieldRow}>
          <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Subtitle</Typography>
          <TextField value={subtitle} onChange={(e) => setSubtitle(e.target.value)} fullWidth size="small" multiline rows={3}
            inputProps={{ dir: 'rtl' }}
            InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
        </Box>
        {/* Duration */}
        <Box sx={{ display: hasDuration ? undefined : 'none' }}>
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Duration</Typography>
            <TextField value={duration} onChange={(e) => setDuration(e.target.value)} fullWidth size="small"
              placeholder="e.g. 4 minutes"
              inputProps={{ dir: 'rtl' }}
              InputProps={{ style: CLINIC_DIALOG_SX.textField as React.CSSProperties }} />
          </Box>
        </Box>
        {/* Text */}
        {hasText && (
          <Box sx={CLINIC_DIALOG_SX.fieldRow}>
            <Typography variant="body2" sx={CLINIC_DIALOG_SX.fieldLabel}>Text</Typography>
            <Box ref={textEditorRef} className="ql-rtl-wrapper" sx={{ flex: 1, '& .ql-container': { minHeight: 120 }, '& .ql-editor': { direction: 'rtl', textAlign: 'right' }, ...colorTooltipSx }}>
              <ReactQuill value={text} onChange={setText} modules={QUILL_MODULES} formats={QUILL_FORMATS} />
            </Box>
          </Box>
        )}
      </Box>
    </ClinicDialog>
  );
};

export default WellnessItemNestedEditDialog;
